import { useEffect, useRef } from "react";
import { Environment } from "../model/internal.config";
import {
  CancelableChannel,
  registerCallbacks,
  registerOptions,
} from "@card-capture-lib/app/CrossDomainChannel";
import { CardCaptureCallbacks, CardCaptureOptions } from "@card-capture-lib/app/model/config.model";

const BASE_HEIGHT = 410;
// 90px per field: half of unified-tokenisation-app's --addPhoneOrEmail: 180px (two fields, same
// layout).
const EMAIL_ONLY_HEIGHT_INCREMENT = 90;

export function getCardCaptureIframeHeight(options: CardCaptureOptions): number {
  return options.cardholderDetailFields === "EMAIL"
    ? BASE_HEIGHT + EMAIL_ONLY_HEIGHT_INCREMENT
    : BASE_HEIGHT;
}

const CARD_CAPTURE_URLS: Record<Environment, string> = {
  LOCAL: import.meta.env.VITE_CARD_CAPTURE_HOST ?? "https://card-capture.dev.littlepay.com",
  DEV: "https://card-capture.dev.littlepay.com",
  QA: "https://card-capture.qa.littlepay.com",
  PROD: "https://card-capture.littlepay.com",
};

export interface CardCaptureIframeProps {
  readonly environment: Environment;
  readonly accessToken: string;
  readonly options: CardCaptureOptions;
  readonly callbacks: CardCaptureCallbacks;
}

export function CardCaptureIframe({
  environment,
  accessToken,
  options,
  callbacks,
}: CardCaptureIframeProps) {
  const cardCaptureUrl = CARD_CAPTURE_URLS[environment];
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    let registerOptionsChannel: CancelableChannel | undefined;
    let registerCallbacksChannel: CancelableChannel | undefined;

    if (iframe) {
      // Ensure CC is always in compact mode
      const optionsCopy = structuredClone(options);
      optionsCopy.uiMode = "COMPACT";

      registerOptionsChannel = registerOptions(
        iframe.contentWindow!,
        cardCaptureUrl,
        optionsCopy,
        accessToken,
      );
      registerCallbacksChannel = registerCallbacks(
        iframe.contentWindow!,
        cardCaptureUrl,
        callbacks,
      );
    }

    return () => {
      registerOptionsChannel?.cancel();
      registerCallbacksChannel?.cancel();
    };
  }, [accessToken, options, callbacks, cardCaptureUrl]);

  return (
    <iframe
      src={cardCaptureUrl}
      title="Card Capture"
      name="Card Capture"
      ref={iframeRef}
      style={{ border: "none", width: "100%", height: getCardCaptureIframeHeight(options) }}
    />
  );
}
